/**
 * Obligation review — the central, risk-bearing screen of the operator
 * responsibility surface.
 *
 * Shows the extracted/structured fields BESIDE the source provision text
 * (provenance always visible) and lets the expert confirm, adjust+confirm,
 * mark needsRevision or reject. Edits never mutate the reviewed version:
 * the server writes a new append-only version (`supersedes` the prior).
 * confirmedBy/confirmedAt are captured server-side; a reviewNote is enforced
 * on edit and reject/needsRevision — the dialog cannot complete without it.
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import Section from "../../components/Section";
import ObligationHazardSection from "./ObligationHazardSection";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI, LegalReviewAction } from "../../LegalAdministrationAPI";
import { ApprovalBlockHint, ApprovalTrail, approveLabel, reviewDialogTexts, useFourEyes } from "./fourEyes";
import {
  ReviewStatusBadge,
  ProvenanceBadge,
  ConfidenceIndicator,
  EffectivePendingWarning,
  obligationFieldLabel,
  regulatedObjectName,
  regulatedSubjectName,
  formatDate,
  formatDateTime,
} from "./legalDisplay";
import ConformityPredicateView from "../../conformity/ConformityPredicateView";
import { useDinUnitResolver } from "../../conformity/useDinUnitResolver";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

interface Properties {
  obligationKey: string;
  vocabulary: any;
  regulatedObjects: any[];
  /** Gefährdungszuordnung — nur in der Administration, nicht auf der Seite „Regelwerke“. */
  showHazards?: boolean;
  onClose: (didChange: boolean) => void;
}

/**
 * Sentinel für „weder Anlage noch Subjektklasse".
 *
 * Ein leeres Auswahlfeld liest sich wie eine Extraktionslücke — dabei ist `regulatedObjectCode:
 * null` bei einer Beauftragten- oder Organisationspflicht eine bewusste Aussage (LEGAL-26): die
 * Pflicht hat keinen Anlagenbezug. 42 Pflichten im Bestand stehen so da. Der Sentinel macht die
 * Aussage wählbar und sichtbar; gespeichert wird weiterhin null in beiden Achsen.
 *
 * Kein Leerstring: Radix-Select verbietet ihn als Item-Wert.
 */
const NO_REGULATED_REF = "__none__";

const EDITABLE_SCALAR_FIELDS = [
  "type",
  "modality",
  "regulatedObjectCode",
  // Zweite Achse — muss mit in die Änderungsliste, sonst wird die Auswahl des Prüfers nicht
  // übermittelt.
  "regulatedSubject",
  "roleCode",
  "protectionGoalCode",
  "description",
];

// Codes known to the bootstrap extraction — baseline suggestions even when the
// vocabulary endpoint doesn't (yet) serve the distinct values.
const KNOWN_ROLE_CODES = [
  "arbeitgeber",
  "arbeitnehmer",
  "betreiber",
  "bevollmaechtigterBezirksschornsteinfeger",
  "eigentuemer",
  "veranlasser",
  "verwalter",
  "wohnungseigentuemer",
];

const KNOWN_PROTECTION_GOAL_CODES = ["arbeitsschutz", "brandschutz", "gesundheitsschutz", "umweltschutz"];

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Beschriftetes Auswahlfeld — Label über dem Feld, wie überall im Panel. */
type SelectOption = { key: string; text: string };
/**
 * Gruppierte Auswahl — EIN Feld, aber die Herkunft der Werte bleibt sichtbar.
 *
 * Beim regulierten Objekt stehen zwei Achsen in einer Liste: 103 Anlagencodes und 9
 * Subjektklassen. Ohne Überschrift verschwinden die neun hinten und sind praktisch unauffindbar —
 * die Vereinigung der Achsen war die Absicht, ihre Ununterscheidbarkeit nicht.
 */
type SelectOptionGroup = { label: string | null; options: SelectOption[] };

const isGrouped = (
  options: SelectOption[] | SelectOptionGroup[],
): options is SelectOptionGroup[] =>
  options.length > 0 && (options as SelectOptionGroup[])[0].options !== undefined;

function LabeledSelect(properties: {
  id: string;
  label: string;
  value?: string;
  disabled?: boolean;
  minWidth: number;
  /** Flache Liste, oder Gruppen mit Überschrift — siehe `SelectOptionGroup`. */
  options: SelectOption[] | SelectOptionGroup[];
  onChange: (value: string) => void;
  /** Entscheidungshilfe unter dem Feld — für Vokabulare, deren Trennlinie man kennen muss. */
  hint?: string;
}) {
  // Leere Gruppen fallen weg. Sonst steht bei stale/fehlendem Vokabular eine Überschrift ohne
  // Einträge da — schlimmer als gar keine Gruppe, weil sie eine Auswahl verspricht, die es nicht
  // gibt.
  const groups: SelectOptionGroup[] = (
    isGrouped(properties.options)
      ? properties.options
      : [{ label: null, options: properties.options }]
  ).filter((group) => group.options.length > 0);
  return (
    <div
      className="flex flex-1 flex-col gap-1.5"
      style={{ minWidth: `min(${properties.minWidth}px, 100%)` }}
    >
      <Label htmlFor={properties.id}>{properties.label}</Label>
      <Select
        value={properties.value ?? ""}
        disabled={properties.disabled}
        onValueChange={properties.onChange}
      >
        <SelectTrigger id={properties.id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-w-[calc(100vw-2rem)]">
          {groups.map((group, index) => (
            <SelectGroup key={group.label ?? `group-${index}`}>
              {index > 0 && <SelectSeparator />}
              {group.label && <SelectLabel>{group.label}</SelectLabel>}
              {group.options.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.text}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {properties.hint && (
        <span className="text-xs text-muted-foreground">{properties.hint}</span>
      )}
    </div>
  );
}

/** Eine Kennzahl im Kopf der Pflicht: kleine Beschriftung, Wert darunter. */
function MetaField(properties: { label: string; children: any }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-xs text-muted-foreground">{properties.label}</span>
      <span className="min-w-0 break-words">{properties.children}</span>
    </div>
  );
}

export default function ObligationReviewPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [versions, setVersions] = useState<any[]>([]);
  const [provision, setProvision] = useState<any>(null);
  const [regulation, setRegulation] = useState<any>(null);
  const [edited, setEdited] = useState<any>(null);
  const [didChange, setDidChange] = useState(false);
  const [noteDialogAction, setNoteDialogAction] = useState<LegalReviewAction | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const current = useMemo(
    () => versions.find((version: any) => !version.validTo) || versions[versions.length - 1],
    [versions],
  );

  // Conformity formalization (LEGAL-NN-C) — rendered only when the obligation
  // carries an embedded `conformity` {applicability, predicate}. Units resolve
  // from the DIN-276 ontology; the fetch is skipped when there is no conformity.
  const unitOf = useDinUnitResolver(!!current?.conformity);

  const load = async () => {
    const requestKey = properties.obligationKey;
    setLoading(true);
    setLoadFailed(false);

    try {
      const result = await legalAPI.getObligationVersions(requestKey);

      if (requestKey !== properties.obligationKey) {
        return;
      }

      const nextVersions = result.versions || [];

      setVersions(nextVersions);
      setProvision(result.provision);
      setRegulation(result.regulation);

      const currentVersion =
        nextVersions.find((version: any) => !version.validTo) || nextVersions[nextVersions.length - 1] || null;

      setEdited(
        currentVersion
          ? {
              ...currentVersion,
              inspection: { ...(currentVersion.inspection || {}) },
            }
          : null,
      );
    } catch (error) {
      if (requestKey !== properties.obligationKey) {
        return;
      }
      setVersions([]);
      setProvision(null);
      setRegulation(null);
      setEdited(null);
      setLoadFailed(true);
      pushError(error);
    } finally {
      if (requestKey === properties.obligationKey) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.obligationKey]);

  const changes = useMemo(() => {
    if (!current || !edited) {
      return {};
    }

    const result: Record<string, any> = {};

    for (const field of EDITABLE_SCALAR_FIELDS) {
      if ((edited[field] ?? null) !== (current[field] ?? null)) {
        result[field] = edited[field] ?? null;
      }
    }

    if (JSON.stringify(edited.inspection || {}) !== JSON.stringify(current.inspection || {})) {
      result.inspection = edited.inspection;
    }

    return result;
  }, [current, edited]);

  const isDirty = Object.keys(changes).length > 0;
  const isReviewable = current && !current.validTo && current.reviewStatus !== "confirmed";
  const fourEyes = useFourEyes(current);
  const noteTexts = reviewDialogTexts(t, noteDialogAction || "confirm", fourEyes.stage, isDirty, properties.obligationKey);
  const confirmTexts = reviewDialogTexts(t, "confirm", fourEyes.stage, false, properties.obligationKey);

  const submitReview = async (action: LegalReviewAction, note: string) => {
    setSubmitting(true);

    try {
      await legalAPI.reviewObligation({
        obligationKey: properties.obligationKey,
        action,
        changes: action === "confirm" && isDirty ? changes : undefined,
        reviewNote: note || undefined,
      });

      setDidChange(true);
      setNoteDialogAction(null);
      setReviewNote("");
      await load();
      pushSuccess(t(`operatorResponsibilityPanel.reviewed.${action}`));
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const startAction = (action: LegalReviewAction) => {
    if (action === "confirm" && !isDirty) {
      // Clean confirm — no note required, attribution captured server-side.
      // The decision is logged and append-only, so it still asks first.
      setConfirmDialogOpen(true);
    } else {
      // Edit-confirm, needsRevision and reject all REQUIRE a review note.
      setReviewNote("");
      setNoteDialogAction(action);
    }
  };

  const selectOptions = (keys: string[], i18nPrefix: string) =>
    keys.map((key) => ({ key, text: t(`${i18nPrefix}.${key}`) }));

  // known codes ∪ server-provided distinct codes ∪ the current value
  const codeOptions = (known: string[], served: string[] | undefined, currentValue: string | undefined, i18nPrefix: string) =>
    Array.from(new Set([...known, ...(served || []), ...(currentValue ? [currentValue] : [])]))
      .sort()
      .map((code) => ({ key: code, text: t(`${i18nPrefix}.${code}`, { defaultValue: code }) }));

  const versionColumns: ColumnDef<any>[] = [
    {
      id: "version",
      accessorFn: (version: any) => version.version,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.version")}</SortableHeader>
      ),
      size: 80,
    },
    {
      id: "validFrom",
      accessorFn: (version: any) => formatDate(version.validFrom),
      header: t("operatorResponsibilityPanel.validFrom"),
      size: 110,
      enableSorting: false,
    },
    {
      id: "validTo",
      accessorFn: (version: any) => formatDate(version.validTo),
      header: t("operatorResponsibilityPanel.validTo"),
      size: 110,
      enableSorting: false,
    },
    {
      id: "status",
      header: t("operatorResponsibilityPanel.status"),
      size: 140,
      enableSorting: false,
      cell: ({ row }) => <ReviewStatusBadge status={row.original.reviewStatus} />,
    },
    {
      id: "reviewedBy",
      accessorFn: (version: any) => version.confirmedBy || version.reviewedBy || "—",
      header: t("operatorResponsibilityPanel.reviewedBy"),
      size: 220,
      enableSorting: false,
    },
    {
      id: "reviewedAt",
      accessorFn: (version: any) => formatDateTime(version.confirmedAt || version.reviewedAt),
      header: t("operatorResponsibilityPanel.reviewedAt"),
      size: 170,
      enableSorting: false,
    },
    {
      id: "changes",
      header: t("operatorResponsibilityPanel.changes"),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="min-w-0 break-words">
          {row.original.reviewerEdited
            ? (row.original.changedFields || [])
                .map((change: any) => obligationFieldLabel(t, change.field))
                .join(", ")
            : row.original.supersedes
              ? t("operatorResponsibilityPanel.supersedesVersion", { version: row.original.supersedes })
              : "—"}
          {row.original.reviewNote ? ` · "${row.original.reviewNote}"` : ""}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <Skeleton className="h-9 w-[280px] max-w-full" />
        <Skeleton className="h-6 w-[320px] max-w-full" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    );
  }

  if (loadFailed || !current || !edited) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h3 className="min-w-0 flex-1 break-words text-lg font-semibold">
            {properties.obligationKey}
          </h3>
          <Button
            variant="outline"
            className="max-sm:w-full"
            onClick={() => properties.onClose(didChange)}
          >
            {t("global.close")}
          </Button>
        </div>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {loadFailed
            ? t("operatorResponsibilityPanel.obligationVersionsLoadFailed")
            : t("operatorResponsibilityPanel.noObligationVersions")}
        </p>
      </div>
    );
  }

  const typeId = `${fieldId}-type`;
  const modalityId = `${fieldId}-modality`;
  const regulatedObjectId = `${fieldId}-regulated-object`;
  const roleCodeId = `${fieldId}-role-code`;
  const protectionGoalId = `${fieldId}-protection-goal`;
  const descriptionId = `${fieldId}-description`;
  const inspectionTriggerId = `${fieldId}-inspection-trigger`;
  const inspectorId = `${fieldId}-inspector`;
  const intervalId = `${fieldId}-interval`;
  const noteId = `${fieldId}-note`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h3 className="min-w-0 break-words text-lg font-semibold">{properties.obligationKey}</h3>
          <ReviewStatusBadge status={current.reviewStatus} />
          <ProvenanceBadge provenanceType={current.provenanceType} generatedBy={current.generatedBy} />
        </div>
        <Button
          variant="outline"
          className="max-sm:w-full"
          onClick={() => properties.onClose(didChange)}
        >
          {t("global.close")}
        </Button>
      </div>

      <EffectivePendingWarning obligation={current} />

      <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-6">
        {/* Source provision — provenance is never hidden */}
        <Section title={t("operatorResponsibilityPanel.sourceProvision")}>
          <p className="min-w-0 break-words font-semibold">
            {provision?.citation || `${current.regulationId} ${current.provisionId}`}
          </p>
          <p className="mt-2 min-w-0 break-words text-xs text-muted-foreground">
            {regulation?.title} · {regulation?.eliId} ·{" "}
            {t(`operatorResponsibilityPanel.bindingForce.${regulation?.bindingForce || "unknown"}`)}
          </p>
          <div className="mt-3 max-h-[560px] min-w-0 overflow-y-auto whitespace-pre-wrap break-words rounded-md border p-3 text-sm leading-relaxed">
            {provision?.text || t("operatorResponsibilityPanel.provisionTextUnavailable")}
          </div>
        </Section>

        {/* Structured fields — editable subset only */}
        <Section title={t("operatorResponsibilityPanel.extractedObligation")}>
          <div className="flex min-w-0 flex-wrap gap-6">
            <MetaField label={t("operatorResponsibilityPanel.confidence")}>
              <ConfidenceIndicator value={current.confidence} />
            </MetaField>
            <MetaField label={t("operatorResponsibilityPanel.validFrom")}>
              {formatDate(current.validFrom)}
            </MetaField>
            <MetaField label={t("operatorResponsibilityPanel.recordedAt")}>
              {formatDateTime(current.recordedAt)}
            </MetaField>
            <MetaField label={t("operatorResponsibilityPanel.version")}>
              {current.version}
              {current.reviewerEdited ? ` (${t("operatorResponsibilityPanel.reviewerEdited")})` : ""}
            </MetaField>
          </div>

          <div className="mt-4 flex min-w-0 flex-wrap gap-4">
            <LabeledSelect
              id={typeId}
              label={t("operatorResponsibilityPanel.type")}
              minWidth={200}
              options={selectOptions(
                properties.vocabulary?.obligationTypes || [],
                "operatorResponsibilityPanel.obligationType",
              )}
              value={edited.type}
              disabled={!isReviewable}
              onChange={(value) => setEdited({ ...edited, type: value })}
            />
            <LabeledSelect
              id={modalityId}
              label={t("operatorResponsibilityPanel.modality")}
              minWidth={160}
              options={selectOptions(
                properties.vocabulary?.modalities || [],
                "operatorResponsibilityPanel.modalityValue",
              )}
              value={edited.modality}
              disabled={!isReviewable}
              onChange={(value) => setEdited({ ...edited, modality: value })}
            />
            <LabeledSelect
              id={regulatedObjectId}
              label={t("operatorResponsibilityPanel.regulatedObject")}
              minWidth={280}
              // EIN Feld über beide Achsen. "Woran hängt die Pflicht?" ist eine Frage an die
              // Norm; ob die Antwort in der Anlagen- oder der Subjekttabelle landet, ist eine
              // Speicherentscheidung und geht den Prüfer nichts an. Die Zuordnung erledigt
              // `onChange` anhand des gewählten Werts.
              // Drei Gruppen statt einer Liste aus 113 Einträgen: die Subjektklassen standen
              // sonst hinter 103 Anlagencodes und waren praktisch unauffindbar. Ein Feld bleibt
              // es — sichtbar wird nur, aus welcher Achse ein Wert stammt.
              options={[
                {
                  label: null,
                  options: [
                    {
                      key: NO_REGULATED_REF,
                      text: t("operatorResponsibilityPanel.noRegulatedReference"),
                    },
                  ],
                },
                {
                  label: t("operatorResponsibilityPanel.subjectClasses"),
                  options: (properties.vocabulary?.regulatedSubjects || []).map(
                    (code: string) => ({ key: code, text: regulatedSubjectName(t, code) }),
                  ),
                },
                {
                  label: t("operatorResponsibilityPanel.plantObjects"),
                  options: (properties.regulatedObjects || []).map((object: any) => ({
                    key: object.code,
                    text: regulatedObjectName(t, object.code, object.name),
                  })),
                },
              ]}
              // Ohne den Sentinel stünde hier ein leeres Feld, und ein Prüfer läse eine
              // bewusste Aussage als fehlende Extraktion.
              value={edited.regulatedSubject || edited.regulatedObjectCode || NO_REGULATED_REF}
              disabled={!isReviewable}
              onChange={(value) => {
                const isNone = value === NO_REGULATED_REF;
                const isSubject = (properties.vocabulary?.regulatedSubjects || []).includes(value);
                setEdited({
                  ...edited,
                  regulatedObjectCode: isNone || isSubject ? null : value,
                  regulatedSubject: isNone || !isSubject ? null : value,
                });
              }}
            />
          </div>

          <div className="mt-4 flex min-w-0 flex-wrap gap-4">
            {/* roleCode / protectionGoalCode are small working vocabularies
                (no schema enum) — plain selects over the codes in use. */}
            <LabeledSelect
              id={roleCodeId}
              label={t("operatorResponsibilityPanel.roleCode")}
              minWidth={280}
              options={codeOptions(
                KNOWN_ROLE_CODES,
                properties.vocabulary?.roleCodes,
                edited.roleCode,
                "operatorResponsibilityPanel.roleCodeValue",
              )}
              value={edited.roleCode}
              disabled={!isReviewable}
              onChange={(value) => setEdited({ ...edited, roleCode: value })}
            />
            <LabeledSelect
              id={protectionGoalId}
              label={t("operatorResponsibilityPanel.protectionGoalCode")}
              minWidth={280}
              options={codeOptions(
                KNOWN_PROTECTION_GOAL_CODES,
                properties.vocabulary?.protectionGoalCodes,
                edited.protectionGoalCode,
                "operatorResponsibilityPanel.protectionGoalValue",
              )}
              value={edited.protectionGoalCode}
              disabled={!isReviewable}
              onChange={(value) => setEdited({ ...edited, protectionGoalCode: value })}
            />
          </div>

          <div className="mt-4 flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={descriptionId}>{t("operatorResponsibilityPanel.description")}</Label>
            <Textarea
              id={descriptionId}
              rows={5}
              value={edited.description || ""}
              disabled={!isReviewable}
              onChange={(event) => setEdited({ ...edited, description: event.target.value })}
            />
          </div>

          <div className="mt-4 flex min-w-0 flex-wrap gap-4">
            <LabeledSelect
              id={inspectionTriggerId}
              label={t("operatorResponsibilityPanel.inspectionTrigger")}
              minWidth={260}
              options={selectOptions(
                properties.vocabulary?.inspectionTriggers || [],
                "operatorResponsibilityPanel.inspectionTriggerValue",
              )}
              // Die Trennlinie, an der die drei neuen Anlässe hängen. Ohne sie ordnet ein Prüfer
              // einen Jahresbericht plausibel als "dauernd geschuldet" ein — das ist der Fall,
              // den das Vokabular gerade auseinanderhalten soll.
              hint={t("operatorResponsibilityPanel.inspectionTriggerHint")}
              value={edited.inspection?.trigger}
              disabled={!isReviewable}
              onChange={(value) =>
                setEdited({ ...edited, inspection: { ...(edited.inspection || {}), trigger: value } })
              }
            />
            <LabeledSelect
              id={inspectorId}
              label={t("operatorResponsibilityPanel.inspector")}
              minWidth={300}
              options={selectOptions(
                properties.vocabulary?.inspectors || [],
                "operatorResponsibilityPanel.inspectorValue",
              )}
              value={edited.inspection?.inspector}
              disabled={!isReviewable}
              onChange={(value) =>
                setEdited({ ...edited, inspection: { ...(edited.inspection || {}), inspector: value } })
              }
            />
            <div className="flex w-[220px] max-w-full flex-col gap-1.5">
              <Label htmlFor={intervalId}>
                {t("operatorResponsibilityPanel.maxIntervalMonths")}
              </Label>
              <Input
                id={intervalId}
                inputMode="numeric"
                value={
                  edited.inspection?.maxIntervalMonths !== undefined && edited.inspection?.maxIntervalMonths !== null
                    ? String(edited.inspection.maxIntervalMonths)
                    : ""
                }
                disabled={!isReviewable}
                onChange={(event) => {
                  const parsed = parseInt(event.target.value || "", 10);
                  const inspection = { ...(edited.inspection || {}) };

                  if (isNaN(parsed)) {
                    delete inspection.maxIntervalMonths;
                  } else {
                    inspection.maxIntervalMonths = parsed;
                  }

                  setEdited({ ...edited, inspection });
                }}
              />
            </div>
          </div>

          {current && !current.validTo ? (
            <div className="mt-6 min-w-0">
              <ApprovalTrail obligation={current} />
            </div>
          ) : null}

          {isReviewable ? (
            <div className="mt-6 flex min-w-0 flex-wrap items-center gap-3">
              <Button
                className="gap-2 max-sm:w-full"
                disabled={submitting || !fourEyes.canApprove}
                onClick={() => startAction("confirm")}
              >
                {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                {approveLabel(t, fourEyes.stage, isDirty)}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={submitting || !fourEyes.canVeto}
                onClick={() => startAction("needsRevision")}
              >
                {t("operatorResponsibilityPanel.needsRevision")}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={submitting || !fourEyes.canVeto}
                onClick={() => startAction("reject")}
              >
                {t("operatorResponsibilityPanel.reject")}
              </Button>
              <ApprovalBlockHint obligation={current} />
              {isDirty ? (
                <p className="min-w-0 break-words text-xs text-warning">
                  {t("operatorResponsibilityPanel.editCreatesNewVersion")}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="mt-6 min-w-0 break-words text-sm text-muted-foreground">
              {current.validTo
                ? t("operatorResponsibilityPanel.versionSuperseded")
                : t("operatorResponsibilityPanel.alreadyConfirmed", {
                    reviewer: current.confirmedBy,
                    date: formatDateTime(current.confirmedAt),
                  })}
            </p>
          )}
        </Section>
      </div>

      {current?.conformity && (
        <Section title={t("conformity.ruleTitle", "Regel & Formalisierung")}>
          <ConformityPredicateView
            mode="rule"
            unitOf={unitOf}
            obligation={{
              obligationKey: current.obligationKey,
              version: current.version,
              regulationId: current.regulationId,
              provisionId: current.provisionId,
              regulatedObjectCode: current.regulatedObjectCode,
              modality: current.modality,
              reviewStatus: current.reviewStatus,
              source: current.conformity?.source || provision?.citation || regulation?.title,
              conformity: current.conformity,
            }}
          />
        </Section>
      )}

      {current && properties.showHazards !== false ? (
        <ObligationHazardSection
          obligationKey={current.obligationKey}
          mitigatesHazard={current.mitigatesHazard}
          onSaved={() => {
            setDidChange(true);
            load();
          }}
        />
      ) : null}

      <Section title={t("operatorResponsibilityPanel.versionHistory")}>
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={versionColumns}
            data={[...versions].sort((a: any, b: any) => b.version - a.version)}
            pageSize={10}
            previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
            nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
            emptyMessage={t("operatorResponsibilityPanel.noObligationVersions")}
          />
        </div>
      </Section>

      {/* Entscheidung mit Anmerkung: Korrektur, Ablehnung und Rückgabe zur
          Überarbeitung sind ohne Begründung später nicht erklärbar. */}
      <Dialog
        open={!!noteDialogAction}
        onOpenChange={(open) => {
          if (!open) setNoteDialogAction(null);
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {noteTexts.title}
            </DialogTitle>
            <DialogDescription className="min-w-0 break-words">
              {noteTexts.consequence}
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={noteId}>{t("operatorResponsibilityPanel.reviewNote")}</Label>
            <Textarea
              id={noteId}
              rows={4}
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
            />
            <p className="min-w-0 break-words text-xs text-muted-foreground">
              {t("operatorResponsibilityPanel.reviewNoteRequired")}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setNoteDialogAction(null)}
            >
              {t("global.cancel")}
            </Button>
            <Button
              className="gap-2 max-sm:w-full"
              disabled={submitting || reviewNote.trim().length === 0}
              onClick={() => submitReview(noteDialogAction as LegalReviewAction, reviewNote.trim())}
            >
              {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
              {noteTexts.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unveränderte Freigabe: keine Anmerkung nötig, die Folge steht dabei. */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTexts.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {confirmTexts.consequence}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                setConfirmDialogOpen(false);
                submitReview("confirm", "");
              }}
            >
              {confirmTexts.submit}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
